import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  updateDoc,
  doc,
  getDoc,
  Timestamp 
} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Serviço para gerenciar movimentações de aluguel
 */

// Registra uma nova saída de aluguel
export const createRentalMovement = async (companyId, rentalData) => {
  try {
    const movementData = {
      companyId,
      rewardId: rentalData.rewardId,
      rewardName: rentalData.rewardName,
      customerId: rentalData.customerId,
      customerName: rentalData.customerName,
      customerDocument: rentalData.customerDocument,
      movementType: 'rental_out', // 'rental_out' ou 'rental_return'
      rentalDuration: rentalData.rentalDuration,
      expectedDeliveryDate: rentalData.expectedDeliveryDate ? Timestamp.fromDate(new Date(rentalData.expectedDeliveryDate)) : null,
      actualDeliveryDate: rentalData.actualDeliveryDate ? Timestamp.fromDate(new Date(rentalData.actualDeliveryDate)) : null,
      expectedReturnDate: rentalData.expectedReturnDate ? Timestamp.fromDate(new Date(rentalData.expectedReturnDate)) : null,
      actualReturnDate: null, // Será preenchido quando devolvido
      status: 'rented', // 'rented', 'returned', 'overdue'
      pointsUsed: rentalData.pointsUsed,
      notes: rentalData.notes || '',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    // Nota: O estoque será decrementado pelo rewardsService quando o resgate for completado
    // Não decrementar aqui para evitar duplicação

    const docRef = await addDoc(collection(db, 'rental_movements'), movementData);
    return { id: docRef.id, ...movementData };
  } catch (error) {
    console.error('Erro ao criar movimentação de aluguel:', error);
    throw error;
  }
};

// Registra a devolução de um item alugado
export const returnRentalItem = async (movementId, returnData) => {
  try {
    const movementRef = doc(db, 'rental_movements', movementId);
    const movementDoc = await getDoc(movementRef);
    
    if (!movementDoc.exists()) {
      throw new Error('Movimentação não encontrada');
    }

    const updateData = {
      actualReturnDate: Timestamp.fromDate(new Date(returnData.actualReturnDate)),
      status: 'returned',
      returnNotes: returnData.notes || '',
      returnCondition: returnData.condition || 'good', // 'good', 'damaged', 'lost'
      updatedAt: Timestamp.now()
    };

    await updateDoc(movementRef, updateData);

    // Criar registro de movimento de devolução
    const returnMovementData = {
      companyId: movementDoc.data().companyId,
      rewardId: movementDoc.data().rewardId,
      rewardName: movementDoc.data().rewardName,
      customerId: movementDoc.data().customerId,
      customerName: movementDoc.data().customerName,
      customerDocument: movementDoc.data().customerDocument,
      movementType: 'rental_return',
      originalMovementId: movementId,
      actualReturnDate: Timestamp.fromDate(new Date(returnData.actualReturnDate)),
      returnCondition: returnData.condition || 'good',
      returnNotes: returnData.notes || '',
      createdAt: Timestamp.now()
    };

    await addDoc(collection(db, 'rental_movements'), returnMovementData);

    // Incrementar estoque do prêmio de volta se tiver controle de estoque
    // (Sempre restaura estoque, independente da condição)
    if (movementDoc.data().rewardId) {
      try {
        const rewardRef = doc(db, 'rewards', movementDoc.data().rewardId);
        const rewardDoc = await getDoc(rewardRef);
        
        if (rewardDoc.exists()) {
          const rewardData = rewardDoc.data();
          
          if (rewardData.hasStockControl !== false) {
            const newStock = (rewardData.stockQuantity || 0) + 1;
            
            await updateDoc(rewardRef, {
              stockQuantity: newStock,
              updatedAt: Timestamp.now()
            });
          }
        }
      } catch (stockError) {
        console.error('Erro ao atualizar estoque:', stockError);
        // Não falhar a operação se não conseguir atualizar estoque
      }
    }

    return true;
  } catch (error) {
    console.error('Erro ao registrar devolução:', error);
    throw error;
  }
};

// Busca todas as movimentações de aluguel de uma empresa
export const getRentalMovements = async (companyId, filters = {}) => {
  try {
    let q = query(
      collection(db, 'rental_movements'),
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc')
    );

    // Aplicar filtros se fornecidos
    if (filters.rewardId) {
      q = query(q, where('rewardId', '==', filters.rewardId));
    }

    if (filters.customerId) {
      q = query(q, where('customerId', '==', filters.customerId));
    }

    if (filters.status) {
      q = query(q, where('status', '==', filters.status));
    }

    const querySnapshot = await getDocs(q);
    const movements = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      movements.push({
        id: doc.id,
        ...data,
        // Converter timestamps para datas
        expectedDeliveryDate: data.expectedDeliveryDate?.toDate() || null,
        actualDeliveryDate: data.actualDeliveryDate?.toDate() || null,
        expectedReturnDate: data.expectedReturnDate?.toDate() || null,
        actualReturnDate: data.actualReturnDate?.toDate() || null,
        createdAt: data.createdAt?.toDate() || null,
        updatedAt: data.updatedAt?.toDate() || null
      });
    });

    return movements;
  } catch (error) {
    console.error('Erro ao buscar movimentações de aluguel:', error);
    throw error;
  }
};

// Busca movimentações ativas (ainda não devolvidas)
export const getActiveRentals = async (companyId) => {
  try {
    const q = query(
      collection(db, 'rental_movements'),
      where('companyId', '==', companyId),
      where('movementType', '==', 'rental_out'),
      where('status', 'in', ['rented', 'overdue']),
      orderBy('expectedReturnDate', 'asc')
    );

    const querySnapshot = await getDocs(q);
    const activeRentals = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const expectedReturn = data.expectedReturnDate?.toDate();
      const now = new Date();
      
      // Verificar se está atrasado
      const isOverdue = expectedReturn && now > expectedReturn;
      
      activeRentals.push({
        id: doc.id,
        ...data,
        expectedDeliveryDate: data.expectedDeliveryDate?.toDate() || null,
        actualDeliveryDate: data.actualDeliveryDate?.toDate() || null,
        expectedReturnDate: expectedReturn,
        actualReturnDate: data.actualReturnDate?.toDate() || null,
        createdAt: data.createdAt?.toDate() || null,
        updatedAt: data.updatedAt?.toDate() || null,
        isOverdue
      });
    });

    return activeRentals;
  } catch (error) {
    console.error('Erro ao buscar aluguéis ativos:', error);
    throw error;
  }
};

// Marca aluguéis como atrasados
export const markOverdueRentals = async (companyId) => {
  try {
    const activeRentals = await getActiveRentals(companyId);
    const now = new Date();
    
    const overdueUpdates = [];
    
    for (const rental of activeRentals) {
      if (rental.expectedReturnDate && now > rental.expectedReturnDate && rental.status !== 'overdue') {
        overdueUpdates.push(
          updateDoc(doc(db, 'rental_movements', rental.id), {
            status: 'overdue',
            updatedAt: Timestamp.now()
          })
        );
      }
    }
    
    if (overdueUpdates.length > 0) {
      await Promise.all(overdueUpdates);
    }
    
    return overdueUpdates.length;
  } catch (error) {
    console.error('Erro ao marcar aluguéis atrasados:', error);
    throw error;
  }
};

// Busca histórico de um cliente específico
export const getCustomerRentalHistory = async (companyId, customerId) => {
  try {
    const q = query(
      collection(db, 'rental_movements'),
      where('companyId', '==', companyId),
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const history = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      history.push({
        id: doc.id,
        ...data,
        expectedDeliveryDate: data.expectedDeliveryDate?.toDate() || null,
        actualDeliveryDate: data.actualDeliveryDate?.toDate() || null,
        expectedReturnDate: data.expectedReturnDate?.toDate() || null,
        actualReturnDate: data.actualReturnDate?.toDate() || null,
        createdAt: data.createdAt?.toDate() || null,
        updatedAt: data.updatedAt?.toDate() || null
      });
    });

    return history;
  } catch (error) {
    console.error('Erro ao buscar histórico do cliente:', error);
    throw error;
  }
};

// Busca estatísticas de aluguel
export const getRentalStats = async (companyId) => {
  try {
    const allMovements = await getRentalMovements(companyId);
    const activeRentals = await getActiveRentals(companyId);
    
    const stats = {
      totalRentals: allMovements.filter(m => m.movementType === 'rental_out').length,
      activeRentals: activeRentals.length,
      overdueRentals: activeRentals.filter(r => r.isOverdue).length,
      returnedRentals: allMovements.filter(m => m.movementType === 'rental_return').length,
      totalRevenue: allMovements.filter(m => m.movementType === 'rental_out').reduce((sum, m) => sum + (m.pointsUsed || 0), 0)
    };
    
    return stats;
  } catch (error) {
    console.error('Erro ao calcular estatísticas:', error);
    throw error;
  }
};
